import {
  ConnectionState,
  GlobalNotificationManager
} from '../../../webmodule-common/other/ui/icons/icon-notification-signal';
import { DevelopmentError } from '../../../webmodule-common/other/development-error';
import {
  getConcurrencyCheck,
  getSessionLock,
  isOptimisticLockOwner,
  optimisticLock,
  optimisticLockRelease
} from './optimistic-lock';
import { getUserInfo, getUserInformativeDisplayName } from './cache-tools';
import { information } from '../../../webmodule-common/other/ui/modal-option';

import { currentUserClaims } from '../../../webmodule-common/other/currentuser-claims';
import { DateTime } from 'luxon';
import { glossary } from '../../../localization/global';
import { InputUnLockResources, ResourceLock } from '../../api/dealer-api-interface-franchisee';
import { LangResourceLock } from '../../../localization/scripts/dealer-franchisee/common/resource-lock-lang';
import { LockResourceResult } from '../../../webmodule-common/other/resource-lock-info';
import { NullPromise } from '../../../webmodule-common/interop/types/misc';
import { serverDateTimeToLocalDateTime } from '../../../webmodule-common/other/datetime-converter';

/**
 * Creates a concurrency check callback for verifying data integrity after lock recovery.
 * Uses the unified server endpoint to check if the local recordVersion matches the server.
 *
 * @param objectType The object type string (e.g., "QUOTE.QUOTE", "PROJECT.PROJECT")
 * @param id The ID of the resource
 * @param getLocalRecordVersion Async function to get the current local recordVersion
 * @returns A callback function that returns true if data is still valid, false if modified
 */
export function createConcurrencyCheck(
  objectType: string,
  id: string,
  getLocalRecordVersion: () => Promise<string>
): () => Promise<boolean> {
  return async () => {
    try {
      const localVersion = await getLocalRecordVersion();
      const serverVersion = await getConcurrencyCheck(objectType, id);
      if (!serverVersion) {
        console.error(`Could not fetch ${objectType} for concurrency check`);
        return false;
      }
      const isValid = serverVersion === localVersion || localVersion === '';
      console.log(`${objectType} concurrency check: local=${localVersion}, server=${serverVersion}, valid=${isValid}`);
      return isValid;
    } catch (error) {
      console.error(`Error during ${objectType} concurrency check:`, error);
      return false;
    }
  };
}

export interface ResourceLockAction {
  resourceId: string;
  resourceType: string;
  dictEntry: string;

  //this is when for one reason or another this browser session time is way over without performing any activity
  //such as a browser sleep in edge we cant control. in this case we must treat our data as state, and shut down.
  onLockRevoked: () => Promise<void>;

  /**
   * Optional callback to verify data concurrency after recovering a lock.
   * This is called when a lock is recovered after disconnection or browser wake.
   * Returns true if local data is still valid (concurrency token matches server),
   * false if data has been modified by another user.
   * If not provided, lock recovery will succeed without concurrency check.
   */
  onConcurrencyCheck?: () => Promise<boolean>;
}

/**
 * Global store for owned locks. Used to release locks via sendBeacon when browser closes/refreshes.
 */
const ownedLockStore: Map<string, ResourceLock> = new Map();

/**
 * Add a lock to the global store when we become the lock owner.
 */
function addToLockStore(resourceId: string, lock: ResourceLock) {
  ownedLockStore.set(resourceId, lock);
}

/**
 * Remove a lock from the global store when releasing.
 */
function removeFromLockStore(resourceId: string) {
  ownedLockStore.delete(resourceId);
}

/**
 * Get the API endpoint URL for releasing locks via beacon.
 * Uses tenantToken from user claims since sendBeacon doesn't support custom headers.
 */
function getUnlockBeaconUrl(): string {
  const apiHost = globalThis.dealerConfiguration?.apiHost ?? '';
  const baseUrl = `${apiHost}/api/Franchisee/ResourceLock/BeaconUnlock`;

  const tenantToken = currentUserClaims().tenantToken;
  const params = new URLSearchParams();
  params.set('tenantToken', tenantToken);

  return `${baseUrl}?${params.toString()}`;
}

/**
 * Release all owned locks using sendBeacon. Called on browser close/refresh.
 */
function releaseAllLocksViaBeacon() {
  if (ownedLockStore.size === 0) return;

  const sessionLock = getSessionLock();
  const locks: InputUnLockResources = {
    locks: Array.from(ownedLockStore.values()).map(lock => ({
      sessionLock: sessionLock,
      resourceId: lock.id
    }))
  };

  const url = getUnlockBeaconUrl();
  // Use text/plain to avoid CORS preflight - server must parse JSON from text body
  const blob = new Blob([JSON.stringify(locks)], { type: 'text/plain' });

  try {
    const success = navigator.sendBeacon(url, blob);
    if (success) {
      console.log(`Released ${ownedLockStore.size} lock(s) via beacon on page unload`);
    } else {
      console.warn('sendBeacon failed to release locks on page unload');
    }
  } catch (error) {
    console.error('Error releasing locks via beacon:', error);
  }
}
globalThis.releaseAllLocksViaBeacon = releaseAllLocksViaBeacon;

// Register beforeunload handler to release locks when browser closes/refreshes
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    console.log(`beforeunload triggered - owned locks: ${ownedLockStore.size}`);
    releaseAllLocksViaBeacon();
  });
  console.log('Resource lock beforeunload handler registered');
}

let locks: FranchiseeResourceLock[] = [];
let lockInterval: NodeJS.Timeout | null = null;
let isSystemOnline = true;
let previousConnectionState: ConnectionState = ConnectionState.Unknown;
let onlineStateUnsubscribe: (() => void) | null = null;

/**
 * Initialize the online/offline listener for the lock system.
 * This is called automatically when the first lock is created.
 */
function initOnlineStateListener() {
  if (onlineStateUnsubscribe) return; // Already initialized

  const manager = GlobalNotificationManager.getInstance();
  isSystemOnline = manager.online;
  previousConnectionState = manager.connectionState;

  onlineStateUnsubscribe = manager.onConnectionStateChange(state => {
    const wasOffline = previousConnectionState === ConnectionState.Offline;

    isSystemOnline = state === ConnectionState.Online;
    previousConnectionState = state;

    // Only recover locks if transitioning from Offline to Online (not from Unknown to Online)
    if (wasOffline && isSystemOnline) {
      // We just came back online - immediately try to recover all owned locks
      console.log('System back online - attempting to recover resource locks...');
      recoverLocksAfterReconnect();
    }
  });
}

/**
 * Attempt to recover all locks that we owned before going offline.
 * This is called when the system comes back online.
 */
async function recoverLocksAfterReconnect() {
  const ownedLocks = locks.filter(l => l.isLockOwner && !l.lockDiscarded);

  for (const lock of ownedLocks) {
    if (!lock.isWorking) {
      lock.working();
      try {
        console.log(`Attempting to recover lock for ${lock.resourceStr}...`);
        const recovered = await lock.refreshLock();
        if (recovered) {
          console.log(`Lock recovered for ${lock.resourceStr}`);
        } else {
          console.log(`Failed to recover lock for ${lock.resourceStr}`);
        }
      } finally {
        lock.notWorking();
      }
    }
  }
}

const intervalFunc = async () => {
  clearLockInterval();

  // Skip lock processing if system is offline
  if (!isSystemOnline) {
    console.log('System offline - skipping lock refresh cycle');
    if (locks.length > 0) createLockInterval();
    return;
  }

  const locksCopy = locks.filter(() => true);

  try {
    for (const item of locksCopy) {
      if (!item.isWorking) {
        //although this function is an async promise, setInterval itself doesn't care that its a promise
        //so we need to ensure that the interval doesn't reprocess the same entities if they are held
        //up for some reason.
        item.working();
        try {
          const valid = await item.acquireLockLoop();

          if (!valid) item.pop();
        } finally {
          item.notWorking();
        }
      }
    }
  } finally {
    if (locks.length > 0) createLockInterval();
  }
};

function createLockInterval() {
  if (!lockInterval) {
    lockInterval = setInterval(intervalFunc, 1000);
  }
}

function clearLockInterval() {
  if (lockInterval) clearInterval(lockInterval);
  lockInterval = null;
}

class FranchiseeResourceLock {
  isWorking = false;
  lockDiscarded = false;

  working() {
    this.isWorking = true;
  }

  notWorking() {
    this.isWorking = false;
  }

  lockExpiry: DateTime;
  isLockOwner: boolean;
  lockOwnerName: string;
  lockOwnerEmail: string;
  actions: ResourceLockAction;
  resourceStr: string;

  constructor(actions: ResourceLockAction) {
    this.isLockOwner = false;
    this.lockOwnerName = '';
    this.lockOwnerEmail = '';
    this.actions = actions;
    this.resourceStr = glossary(actions.dictEntry);
  }

  private async acquireLock(): NullPromise<ResourceLock> {
    const thislock = await optimisticLock(this.actions.resourceType, this.actions.resourceId);
    this.lockOwnerName = '';
    this.isLockOwner = isOptimisticLockOwner(thislock);
    if (thislock) {
      const info = await getUserInfo(thislock?.systemUserId);
      this.lockOwnerName = info?.friendlyName ?? '';
      this.lockOwnerEmail = info?.emailAddress ?? '';
      this.lockExpiry = serverDateTimeToLocalDateTime(thislock.optmisticLock);
    }
    return thislock;
  }

  private push() {
    initOnlineStateListener(); // Ensure we're listening for online/offline events
    locks.push(this);
    if (lockInterval === null) createLockInterval();
  }

  public pop() {
    locks = locks.filter(x => x !== this);
    if (locks.length === 0) clearLockInterval();
  }

  public async acquire(): Promise<boolean> {
    const lock = await this.acquireLock();

    if (lock === null) {
      console.log(`${this.resourceStr} Lock Failed: Reason Unknown`);
      await information(
        LangResourceLock.lockFailedCouldNotBeLocked({ resource: this.resourceStr }),
        LangResourceLock.resourceLockFailed()
      );
      return false;
    } else if (lock != null) {
      if (this.isLockOwner) {
        console.log(`${this.resourceStr} Lock Success`);
        addToLockStore(this.actions.resourceId, lock);
        this.push();
      } else {
        console.log(`${this.resourceStr} Lock Failed: Reason Already Locked`);
      }
    }
    return true;
  }

  public async release(): Promise<boolean> {
    this.pop();
    if (this.isLockOwner) {
      removeFromLockStore(this.actions.resourceId);
      try {
        await optimisticLockRelease(this.actions.resourceId);
      } catch {
        //we dont want to crash the workflow if lock release fails
      }
      console.log(`${this.resourceStr} Lock Released`);
    }
    return true;
  }

  /**
   * Refresh/recover the lock after coming back online or browser wake.
   * Also verifies data concurrency if onConcurrencyCheck callback is provided.
   * Returns true if lock was successfully recovered and data is still valid, false otherwise.
   */
  public async refreshLock(): Promise<boolean> {
    if (!this.isLockOwner) return false;

    const lock = await this.acquireLock();
    if (this.isLockOwner) {
      console.log(`${this.resourceStr} Lock Recovered after reconnect`);

      // Verify data concurrency if callback is provided
      if (this.actions.onConcurrencyCheck) {
        try {
          const isConcurrencyValid = await this.actions.onConcurrencyCheck();
          if (!isConcurrencyValid) {
            console.log(`${this.resourceStr} Lock Recovered but data was modified - revoking`);
            this.lockDiscarded = true;
            this.pop();
            await information(LangResourceLock.lockDataModifiedWhileOffline({ resource: this.resourceStr }));
            await this.actions.onLockRevoked();
            return false;
          }
          console.log(`${this.resourceStr} Concurrency check passed - data unchanged`);
        } catch (error) {
          console.error(`${this.resourceStr} Concurrency check failed with error:`, error);
          // If we can't verify, fail safe - revoke the lock
          this.lockDiscarded = true;
          this.pop();
          await information(LangResourceLock.lockConcurrencyCheckFailed({ resource: this.resourceStr }));
          await this.actions.onLockRevoked();
          return false;
        }
      }

      return true;
    } else {
      // Lock was taken by someone else while we were offline
      if (lock != null) {
        this.lockDiscarded = true;
        this.pop();
        console.log(`${this.resourceStr} Lock Lost: Taken by another user while offline`);
        await information(
          LangResourceLock.lockFailedTakenByAnother({
            resource: this.resourceStr,
            user: await getUserInformativeDisplayName(lock.systemUserId)
          })
        );
        await this.actions.onLockRevoked();
      }
      return false;
    }
  }

  public async acquireLockLoop(): Promise<boolean> {
    // Skip lock processing if system is offline - don't abandon or fail locks while offline
    if (!isSystemOnline) {
      console.log(`${this.resourceStr} Lock refresh skipped - system offline`);
      return true; // Return true to keep the lock in the list, we'll recover when online
    }

    const seconds = this.lockExpiry.diffNow('seconds').seconds;
    if (seconds < 60) {
      // Browser was likely asleep if timer drifted past expiry
      if (seconds < 0) {
        console.log(`${this.resourceStr} Lock timer expired by ${Math.abs(seconds)} seconds - attempting recovery...`);

        // Try to recover the lock instead of immediately abandoning
        const recovered = await this.refreshLock();
        if (!recovered) {
          // refreshLock already handled the revocation and user notification
          return false;
        }
        // Lock was recovered and concurrency verified
        return true;
      }
      //reaquire lock.
      const lock = await this.acquireLock();
      if (!this.isLockOwner) {
        if (lock == null) {
          this.pop();
          //if we reach here, there was a communication error with the server, so we dont want to close or abandon at this time.
          console.log(`${this.resourceStr} Lock Failed: Reason Unknown`);
          await information(LangResourceLock.lockFailedErrorReacquiring({ resource: this.resourceStr }));
          return false;
        } else {
          this.lockDiscarded = true;
          this.pop();
          console.log(`${this.resourceStr} Lock Failed: Lock Taken by another system`);
          await information(
            LangResourceLock.lockFailedTakenByAnother({
              resource: this.resourceStr,
              user: await getUserInformativeDisplayName(lock.systemUserId)
            })
          );
          await this.actions.onLockRevoked();

          return false;
        }
      } else {
        console.log(`${this.resourceStr} Lock Refreshed`);
        return true;
      }
    } else return true;
  }
}

/**
 *
 * @param action
 */
export async function requestResourceAccess(action: ResourceLockAction): Promise<LockResourceResult> {
  const l = new FranchiseeResourceLock(action);
  const accessResource = await l.acquire();
  const isLockOwner = l.isLockOwner;
  return {
    canUseResource: accessResource,
    isLockOwner: isLockOwner,
    lockOwnerName: l.lockOwnerName,
    lockOwnerEmail: l.lockOwnerEmail
  };
}

/**
 *
 * @param action this must be the exact instance passed to requestResourceAccess. this will release the lock
 * and remove all timed refreshers
 */
export async function releaseResourceAccess(action: ResourceLockAction): Promise<void> {
  const l = locks.find(x => x.actions === action);
  if (l) {
    await l.release();
  }
}

export class ResourceLocker implements LockResourceResult {
  lockAction: ResourceLockAction;
  lockResult: LockResourceResult | null = null;

  constructor(
    id: string,
    restype: string,
    dictEntry: string,
    lockRevokedEvent: () => Promise<void>,
    concurrencyCheckEvent?: () => Promise<boolean>
  ) {
    this.lockAction = {
      resourceId: id,
      resourceType: restype,
      dictEntry: dictEntry,
      onLockRevoked: lockRevokedEvent,
      onConcurrencyCheck: concurrencyCheckEvent
    };
  }

  public get canUseResource(): boolean {
    if (!this.lockResult) throw new DevelopmentError('call lock before checking result');
    return this.lockResult?.canUseResource ?? false;
  }

  public get isLockOwner(): boolean {
    if (!this.lockResult) throw new DevelopmentError('call lock before checking result');
    return this.lockResult?.isLockOwner ?? false;
  }

  public get lockOwnerName(): string {
    if (!this.lockResult) throw new DevelopmentError('call lock before checking result');
    return this.lockResult?.lockOwnerName ?? '';
  }

  public get lockOwnerEmail(): string {
    if (!this.lockResult) throw new DevelopmentError('call lock before checking result');
    return this.lockResult?.lockOwnerEmail ?? '';
  }

  public async lock(): Promise<boolean> {
    this.lockResult = await requestResourceAccess(this.lockAction);
    return this.lockResult.canUseResource ?? false;
  }

  public async release(): Promise<void> {
    if (!this.lockResult) return;
    await releaseResourceAccess(this.lockAction);
    //we have had issues sometimes where the lock released and then code tried
    //to access it. a release lock is basically the same as not being lock owner
    //so this will give readonly access and no errors
    this.lockResult = {
      canUseResource: true,
      isLockOwner: false,
      lockOwnerName: '',
      lockOwnerEmail: ''
    };
  }
}
